import { FC, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import BreadCrumb from "../../components/Breadcrumbs/Breadcrumbs";
import Invoice from '../../components/Invoice/Invoice';
import { Alert, AlertTitle, Box, Button, Chip, CircularProgress, Container, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import jwt_decode from 'jwt-decode';
import AppBar from "@mui/material/AppBar";
import { fetchInvoiceById, fetchInvoicePreview, regenerateInvoicePdf } from '../../store/invoiceStore/invoiceThunk';
import { InvoicePreviewPayload, PdfGenerationError } from "../../utils/invoice.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import HomeHeader from "../SignIn/HomeHeader";
import LoadingButton from "@mui/lab/LoadingButton";
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import CustomizedEstimateMenus from "../../components/dropdownbutton/estimateActionMenu";
import { Utils, VocuherCategory, VocuherTypes } from "../../utils/common-utils";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InvoicePdfComparison from '../../components/Invoice/InvoicePdfComparison';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { toast } from 'react-toastify';
import InvoicePaymentBreakdown, { useInvoicePayments } from '../../components/Invoice/InvoicePaymentBreakdown';

// Shown when the backend could not (re)generate the PDF: lists the missing/invalid fields and the template error hint.
export const PdfIssuePanel: FC<{ error: PdfGenerationError }> = ({ error }) => {
  const issues = error.missingFields || [];
  const blocking = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity !== 'error');
  const detail = error.errorDetail;
  return (
    <Alert severity="error" sx={{ textAlign: 'left', mt: 2, width: '100%', maxWidth: 640 }}>
      <AlertTitle>PDF could not be generated</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>{error.message}</Typography>
      {blocking.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Missing / invalid data (must fix):</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {blocking.map(i => (
              <li key={i.field}>
                <Chip size="small" color="error" label={i.label} sx={{ mr: 1, mb: 0.5 }} />
                <Typography component="span" variant="body2">{i.message}</Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
      {warnings.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Incomplete data (may cause blank sections or errors):</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {warnings.map(i => (
              <li key={i.field}>
                <Chip size="small" color="warning" variant="outlined" label={i.label} sx={{ mr: 1, mb: 0.5 }} />
                <Typography component="span" variant="body2">{i.message}</Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
      {detail && (detail.hint || detail.message) && (
        <Box>
          <Typography variant="subtitle2">Template error:</Typography>
          {detail.hint && <Typography variant="body2">{detail.hint}</Typography>}
          {detail.missingProperty && (
            <Typography variant="body2">Field: <code>{detail.missingProperty}</code>{detail.templateLine ? ` (template line ${detail.templateLine})` : ''}</Typography>
          )}
          {detail.templateSnippet && (
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#fff3f3', p: 1, borderRadius: 1, m: 0 }}>{detail.templateSnippet}</Typography>
          )}
          {!detail.hint && detail.message && (
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#fff3f3', p: 1, borderRadius: 1, m: 0 }}>{detail.message}</Typography>
          )}
        </Box>
      )}
    </Alert>
  );
};

const ACCENT = '#059669';

/** The paper sheets behind the message: a tilted mini invoice with a second sheet peeking out. */
const InvoiceMockup: FC<{ muted?: boolean }> = ({ muted }) => {
  const bar = (w: string | number, h = 7, c = '#e2e8f0') => (
    <Box sx={{ width: w, height: h, borderRadius: 4, bgcolor: c }} />
  );
  return (
    <Box sx={{ position: 'relative', width: { xs: 190, md: 230 }, height: { xs: 210, md: 250 } }}>
      {/* Back sheet, just peeking out. */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, borderRadius: '18px', bgcolor: '#fff',
        transform: 'rotate(-9deg) translate(-16px, 8px)',
        boxShadow: '0 14px 34px rgba(16,64,40,0.10)', opacity: 0.85,
      }} />
      {/* Front sheet with a simplified invoice inside. */}
      <Box
        sx={{
          position: 'absolute', inset: 0, borderRadius: '18px', bgcolor: '#fff',
          transform: 'rotate(4deg)', p: 2, display: 'flex', flexDirection: 'column',
          boxShadow: '0 22px 48px rgba(16,64,40,0.16)',
          filter: muted ? 'grayscale(0.5)' : 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75 }}>
          <Stack spacing={0.75}>
            {bar(58, 9, muted ? '#cbd5e1' : ACCENT)}
            {bar(78)}
          </Stack>
          <Box sx={{
            width: 30, height: 30, borderRadius: '9px',
            bgcolor: muted ? '#f1f5f9' : '#e7f6ef',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 17, color: muted ? '#94a3b8' : ACCENT }} />
          </Box>
        </Stack>
        {/* Table header + rows. */}
        <Box sx={{ height: 16, borderRadius: 1, bgcolor: '#f1f5f9', mb: 1 }} />
        <Stack spacing={1.1} sx={{ flex: 1 }}>
          {[92, 100, 84, 96].map((w, i) => (
            <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
              {bar(`${w * 0.55}%`)}
              {bar(26, 7, '#eef2f6')}
            </Stack>
          ))}
        </Stack>
        {/* Total pill. */}
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.25 }}>
          <Box sx={{
            px: 1.25, py: 0.6, borderRadius: 5,
            bgcolor: muted ? '#f1f5f9' : '#e7f6ef',
          }}>
            {bar(44, 8, muted ? '#cbd5e1' : ACCENT)}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

/**
 * Empty / failed state shown in place of the PDF viewer: a floating invoice mockup on a soft
 * green wash, so the area reads as a document waiting to be built rather than a blank box.
 */
export const PdfMissingState: FC<{
  pdfError: PdfGenerationError | null;
  canRegenerate: boolean;
  isRegenerating: boolean;
  isLoggedIn: boolean;
  onRegenerate: () => void;
  /** Voucher type of the document being previewed ("Quotation", "Purchase Order", ...),
   *  so the heading names what the user is actually looking at. Falls back to "Invoice". */
  documentLabel?: string;
}> = ({ pdfError, canRegenerate, isRegenerating, isLoggedIn, onRegenerate, documentLabel }) => (
  <Box sx={{
    flex: 1, position: 'relative', overflow: 'auto',
    background: 'radial-gradient(120% 85% at 50% 0%, #e8f8f0 0%, #f6fdfa 45%, #ffffff 100%)',
  }}>
    {/* Ambient glow, keeps the large empty area from looking flat. */}
    <Box aria-hidden sx={{
      position: 'absolute', top: -120, left: '50%', width: 620, height: 620,
      transform: 'translateX(-50%)', borderRadius: '50%', pointerEvents: 'none',
      background: 'radial-gradient(circle, rgba(5,150,105,0.14) 0%, rgba(5,150,105,0) 68%)',
      filter: 'blur(14px)',
    }} />

    <Box sx={{
      position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      px: 3, py: { xs: 4, md: 6 },
    }}>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <InvoiceMockup muted={!!pdfError} />
      </Box>

      {/* Without a subtitle under it the heading needs the full gap itself,
          otherwise it sits almost flush against the Generate button. */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: '#0f172a', mb: pdfError ? 1 : 3 }}
      >
        {pdfError
          ? `${documentLabel || 'Invoice'} PDF generation failed`
          : `Missing ${documentLabel || 'Invoice'}`}
      </Typography>
      {/* Only the error state gets a subtitle — the idle state's heading and
          the Generate button already say everything it needs to. */}
      {pdfError && (
        <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 420, mb: 3 }}>
          Fix the fields listed below, then try again.
        </Typography>
      )}

      {canRegenerate ? (
        <LoadingButton
          variant="contained" size="large" loading={isRegenerating}
          startIcon={<AutorenewIcon />} onClick={onRegenerate}
          sx={{
            borderRadius: '12px', px: 4, py: 1.35, fontWeight: 600,
            textTransform: 'none', fontSize: '1rem',
            bgcolor: ACCENT, boxShadow: '0 10px 24px rgba(5,150,105,0.32)',
            '&:hover': { bgcolor: '#047857', boxShadow: '0 12px 28px rgba(5,150,105,0.4)' },
          }}
        >
          {pdfError ? 'Try Again' : 'Generate PDF'}
        </LoadingButton>
      ) : (
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {isLoggedIn ? 'No PDF template is selected for this invoice.' : 'Please ask the sender to regenerate this invoice.'}
        </Typography>
      )}

      {pdfError && <PdfIssuePanel error={pdfError} />}
    </Box>
  </Box>
);

// Preview Invoice page.
const InvoicePreview: FC = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const voucherType = searchParams.get("type") || '';
  const showInLine = true;

  const dispatch = useAppDispatch();
 
  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const { accountDetails } = useAppSelector(state => state.account);
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);

  const [pdfLocation, setPdfLocation] = useState<string>("");
  const [showPayButton, setShowPayButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefersh, setIsRefersh] = useState("");
  // PDF (re)generation state: the stored PDF is missing / broken, a regeneration is running,
  // and the structured failure reason returned by the backend (missing fields etc.).
  const [pdfMissing, setPdfMissing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [pdfError, setPdfError] = useState<PdfGenerationError | null>(null);
  const [viewerKey, setViewerKey] = useState(0);
  // Previous PDF kept after a regeneration, so the two versions can be wiped between.
  const [previousPdfLocation, setPreviousPdfLocation] = useState<string>("");
  const [showComparison, setShowComparison] = useState(false);

  // Payments for the breakdown; the public token link (no templateId, no login) skips the call.
  const { payments, loading: paymentsLoading } = useInvoicePayments(isLoggedIn && templateId ? invoiceId : undefined, voucherType === VocuherCategory.PURCHASE, isRefersh);

  // Regeneration needs an authenticated user and a template; the public token link has neither.
  const canRegenerate = isLoggedIn && !!templateId && !!invoiceId;

  const handleRegeneratePdf = async () => {
    if (!canRegenerate) return;
    setIsRegenerating(true);
    setPdfError(null);
    const result: any = await dispatch(regenerateInvoicePdf({ invoiceId, templateId }));
    setIsRegenerating(false);
    if (regenerateInvoicePdf.fulfilled.match(result) && result.payload?.Location) {
      setPdfLocation(result.payload.Location);
      setPdfMissing(false);
      setViewerKey(k => k + 1);
      const prev = result.payload.previousLocation;
      setPreviousPdfLocation(prev && prev !== result.payload.Location ? prev : "");
      setShowComparison(false);
      // Migrated invoices carry 'DEFAULT' / a stale template id; the backend resolves a real one
      // and saves it on the invoice. Mirror it in the URL so a refresh keeps working.
      const resolvedTemplateId = result.payload.templateId;
      if (resolvedTemplateId && resolvedTemplateId !== templateId) {
        const next = new URLSearchParams(searchParams);
        next.set('templateId', resolvedTemplateId);
        setSearchParams(next, { replace: true });
      }
      toast.success('PDF generated successfully');
      return;
    }
    const err: PdfGenerationError = result.payload || { message: 'PDF generation failed.' };
    setPdfError(err);
    setPdfMissing(true);
    toast.error(err.message || 'PDF generation failed.');
  };

  // Mobile browsers (esp. Android Chrome) won't render a PDF inline via <object>; they show a
  // download placeholder. For those we embed the PDF through Google's viewer so it previews inline.
  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Direct thermal print
  const handleThermalPrint = async () => {
    if (!invoiceDetails) {
      toast.error('Invoice details not loaded');
      return;
    }
    
    try {
      const printContent = generateThermalContent(invoiceDetails);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Unable to open print window');
      }
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
      
      toast.success('Thermal print started');
    } catch (error: any) {
      toast.error('Print failed');
    }
  };

  const generateThermalContent = (invoice: any) => {
    const businessName = selectedAccount?.name || 'Business';
    const businessPhone = selectedAccount?.whatsAppNumber || '';
    const businessAddress = selectedAccount?.location?.address || '';
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Thermal Receipt</title>
          <style>
            @page { size: 58mm auto; margin: 2mm; }
            body {
              font-family: monospace;
              font-size: 8pt;
              line-height: 1.2;
              margin: 0;
              padding: 2mm;
              width: 54mm;
            }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 2mm; margin-bottom: 2mm; }
            .business-name { font-weight: bold; font-size: 10pt; margin-bottom: 1mm; }
            .section { margin-bottom: 2mm; border-bottom: 1px dotted #999; padding-bottom: 1mm; }
            .total { font-weight: bold; text-align: center; padding: 2mm; border: 2px solid #000; margin: 2mm 0; }
            @media print { body { margin: 0; padding: 1mm; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="business-name">${businessName}</div>
            ${businessAddress ? `<div>${businessAddress}</div>` : ''}
            ${businessPhone ? `<div>Ph: ${businessPhone}</div>` : ''}
            <div>================================</div>
            <div><strong>${invoice.voucherType?.toUpperCase()}</strong></div>
                        <div>Bill: ${(invoice as any).invoiceNumber || 'N/A'}</div>
            <div>${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}</div>
          </div>
          
          <div class="section">
            <div><strong>BILL TO:</strong></div>
            <div>${invoice.client?.name || invoice.supplier?.name || 'Customer'}</div>
            ${invoice.client?.phone || invoice.supplier?.phone ? `<div>${invoice.client?.phone || invoice.supplier?.phone}</div>` : ''}
          </div>
          
          <div class="section">
            <div><strong>ITEMS:</strong></div>
            ${invoice.lineItems?.map((item: any, i: number) => `
              <div style="margin: 1mm 0;">
                <div>${item.description || item.name || 'Item ' + (i + 1)}</div>
                <div>${item.qty || 1} x ${Utils.formatNumber(item.appliedRate || item.rate || 0)} = ${Utils.formatNumber(item.total || 0)}</div>
              </div>
            `).join('') || `<div>Service: ${Utils.formatNumber(invoice.amount || 0)}</div>`}
          </div>
          
          <div class="total">
            TOTAL: ₹${Utils.formatNumber(invoice.amount || 0)}
          </div>
          
          <div style="text-align: center; margin-top: 2mm; border-top: 2px dashed #000; padding-top: 2mm;">
            <div>Thank You!</div>
            <div>Powered by iBook</div>
          </div>
        </body>
      </html>
    `;
  };

  useEffect(() => {
    if (voucherType == VocuherCategory.PURCHASE) {
      setShowPayButton(true);
    }

    // Stops a pending PDF wait once the user leaves this invoice.
    let cancelled = false;
    const getPreview = async () => {
      let reqObj: InvoicePreviewPayload = { invoiceId: invoiceId, templateId: templateId };
      let invoicePreviewResponse: any;
      let entry: any = null;
      // A just-saved invoice's PDF is rebuilt in the background: keep loading (up to ~60s) rather than show the old one.
      for (let tries = 0; ; tries++) {
        invoicePreviewResponse = await dispatch(fetchInvoicePreview(reqObj));
        if (cancelled) return;
        entry = Array.isArray(invoicePreviewResponse?.payload) ? invoicePreviewResponse.payload[0] : invoicePreviewResponse?.payload;
        if (!entry?.pdfStale || tries >= 20) break;
        setIsLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (cancelled) return;
      }
      const location = entry?.pdfStale ? null : (entry?.Location || null);
      setPdfLocation(location || "");
      // No stored PDF for this template, the S3 file behind it is gone, or the rebuild never finished -> offer "Generate PDF".
      setPdfMissing(!location || !!entry?.pdfMissing);
      setPdfError(null);
      setPreviousPdfLocation("");
      setShowComparison(false);
      setIsLoading(false);
    };

    const getInvoiceDetails = async () => {
      if (invoiceId) {
        dispatch(fetchInvoiceById(invoiceId));
      }
    }

    if (templateId) {
      getPreview();
      if(isLoggedIn) {getInvoiceDetails();}
    } else {
      if (invoiceId) {
        try {
          let token: any = jwt_decode(invoiceId);
          setPdfLocation(token.pdfLcation);
          setIsLoading(false);
        } catch (error) {
          console.log("Failed! Invalid Token", error);
        }
      }
    }
    return () => { cancelled = true; };
  }, [invoiceId, isRefersh])

  const actionChangeHandler = (data: any) => {
    setIsRefersh(data.isRefersh);
  }

  return (
      <>
        <Box sx={{ 
          backgroundColor: '#fafafb', 
          minHeight: '100vh',
          pt: { xs: 10, sm: 9, md: 9 }, // Increased padding to account for AppBar (56px + spacing)
          pb: 2
        }}>
          <Grid container spacing={2} sx={{ 
            p: { xs: 1, sm: 2 }
          }}>
          {/* Left side - PDF viewer. Full width for the public (not-logged-in) WhatsApp view,
              since the right-hand details panel is hidden there. */}
          <Grid item xs={12} md={isLoggedIn ? 8 : 12} sx={{
            order: { xs: 2, md: 1 },
            mt: { xs: 2, md: 0 } // Add margin top on mobile after header section
          }}>
            {(isLoading ? 
              <Box sx={{ 
                backgroundColor: '#fff', 
                borderRadius: '12px', 
                p: 3, 
                height: { xs: '60vh', md: '85vh' } 
              }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info"/>
                    <Typography sx={{ mt: 2 }}>Loading Preview ...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
              :
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                height: { xs: '85vh', md: '85vh' },
                display: 'flex',
                flexDirection: 'column'
              }}>
                {canRegenerate && !pdfMissing && previousPdfLocation && (
                  // Only the compare toggle lives here now; the regenerate escape hatch
                  // stays on the pdfMissing state, where it is actually needed.
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}
                    sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid #e9ecf1', bgcolor: '#fafafb' }}>
                    {previousPdfLocation && (
                      <Button size="small" variant={showComparison ? 'contained' : 'outlined'}
                        startIcon={<CompareArrowsIcon />}
                        onClick={() => setShowComparison(v => !v)}
                        sx={{ textTransform: 'none' }}>
                        {showComparison ? 'Exit compare' : 'Compare with previous'}
                      </Button>
                    )}
                  </Stack>
                )}
                {showComparison && previousPdfLocation ? (
                  <Box sx={{ flex: 1, p: 1 }}>
                    <InvoicePdfComparison
                      beforeUrl={previousPdfLocation}
                      afterUrl={pdfLocation}
                      height="100%"
                    />
                  </Box>
                ) : pdfMissing ? (
                  <PdfMissingState
                    pdfError={pdfError}
                    canRegenerate={canRegenerate}
                    isRegenerating={isRegenerating}
                    isLoggedIn={isLoggedIn}
                    onRegenerate={handleRegeneratePdf}
                    documentLabel={invoiceDetails?.voucherType}
                  />
                ) : isMobile ? (
                  // Google's embedded viewer renders the PDF inline on mobile (with its own
                  // download/print controls) instead of forcing an "Open" download prompt.
                  <iframe
                    key={viewerKey}
                    title="Invoice PDF"
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(pdfLocation)}&embedded=true`}
                    style={{ flex: 1, width: "100%", border: "none" }}
                  />
                ) : (
                  <object key={viewerKey} style={{ flex: 1, width: "100%" }} data={pdfLocation} type="application/pdf">
                    {/* Browsers render this fallback when the PDF fails to load (404 / blocked). */}
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', p: 3, textAlign: 'center' }}>
                      <PictureAsPdfOutlinedIcon sx={{ fontSize: 56, color: '#b0b7c3', mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Invoice PDF could not be loaded</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The PDF file seems to be missing or broken.
                      </Typography>
                      {canRegenerate && (
                        <LoadingButton variant="contained" color="primary" loading={isRegenerating}
                          startIcon={<AutorenewIcon />} onClick={handleRegeneratePdf}>
                          Generate PDF
                        </LoadingButton>
                      )}
                    </Box>
                  </object>
                )}
              </Box>
            )}
          </Grid>
          
          {/* Right side - Header and Party Details. Hidden for the public (not-logged-in)
              WhatsApp view, where there is no invoice data and the fields just show N/A. */}
          {isLoggedIn && (
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
            {/* Header Section */}
            <Box sx={{ 
              borderRadius: '12px', 
              border: '1px solid #e9ecf1', 
              backgroundColor: '#fff', 
              mb: 2 
            }}>
              <Box sx={{ 
                bgcolor: '#e9ecf1', 
                p: { xs: 1.5, sm: 2 }, 
                borderTopLeftRadius: '12px', 
                borderTopRightRadius: '12px' 
              }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={1} 
                  alignItems={{ xs: 'stretch', sm: 'center' }} 
                  justifyContent="space-between"
                >
                  <Button
                    variant="contained"
                    startIcon={<NavigateBeforeOutlinedIcon />}
                    onClick={() => {
                      const cancelLink = invoiceDetails.voucherCategory == VocuherCategory.SALE ? `/sales` : `/purchases`;
                      navigate(cancelLink);
                    }}
                    sx={{ 
                      backgroundColor: '#f5f5f5', 
                      color: '#545454',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    size="small"
                  >
                    Back
                  </Button>
                  <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    {invoiceDetails && <CustomizedEstimateMenus type={voucherType} row={invoiceDetails} actionChangeHandler={actionChangeHandler} showInlineAction={true}/>}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocalPrintshopIcon />}
                    onClick={handleThermalPrint}
                    disabled={!invoiceDetails}
                    sx={{
                      borderColor: '#4caf50',
                      color: '#4caf50',
                      ml: 1,
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(76, 175, 80, 0.04)'
                      }
                    }}
                  >
                    Thermal Print
                  </Button>
                </Stack>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  {invoiceDetails.voucherType} Preview
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  {invoiceDetails.invoiceNumber}
                </Typography>
              </Box>
            </Box>
            
            {/* Party Details Section */}
            <Box sx={{ 
              borderRadius: '12px', 
              border: '1px solid #e9ecf1', 
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: { xs: 1.5, sm: 2 }, 
                borderTopLeftRadius: '12px', 
                borderTopRightRadius: '12px' 
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  Party Details
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={{ xs: 1.5, sm: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Party Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceDetails?.customerVendorName || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Contact</Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.customerVendorPhone || 'No contact available'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.customerVendorEmail || 'No email available'}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Amount</Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}
                    >
                      {invoiceDetails?.totalAmount?.toLocaleString('en-IN') || '0'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.dueDate ? new Date(invoiceDetails.dueDate).toLocaleDateString('en-IN') : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500,
                        color: invoiceDetails?.paymentStatus === 'PAID' ? 'success.main' : 'warning.main'
                      }}
                    >
                      {invoiceDetails?.paymentStatus || 'PENDING'}
                    </Typography>
                  </Box>

                  <InvoicePaymentBreakdown
                    payments={payments}
                    loading={paymentsLoading}
                    receivedAmount={Number(invoiceDetails?.receivedAmount) || 0}
                    balanceAmount={Number(invoiceDetails?.balanceAmount) || 0}
                  />
                </Stack>
              </Box>
            </Box>
          </Grid>
          )}
          </Grid>
        </Box>
        {/* <Grid container rowSpacing="24" columnSpacing="5">
          <Container component="main" >
            {!isLoggedIn &&
              <div style={{ "width": "70vw" }}>
                <div className="preview">
                  <HomeHeader />
                </div>
                <Box onClick={() => { navigate("/") }}
                  style={{ "float": "right", "marginTop": "-42" }}
                  sx={{ cursor: "pointer" }}>
                  <Button
                    disableElevation
                    sx={{ mt: "20px" }}
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary">
                    Login/Signup
                  </Button>
                </Box>
                <p style={{ "clear": "both" }} />
              </div>
            }
            <Box style={{ "width": "70vw" }}>
              <Stack direction="row"
                justifyContent="space-between"
                alignItems="baseline" sx={{ mb: { xs: 0.9, sm: 0.9 } }}
                style={{ "float": "right" }}>
                <Box onClick={() => { }}
                  sx={{ cursor: "pointer" }}
                >
                  {showPayButton &&
                    <Button
                      disableElevation
                      sx={{ mt: "20px" }}
                      size="large"
                      type="submit"
                      variant="contained"
                      color="primary">
                      Pay
                    </Button>}
                </Box>
              </Stack>
            </Box>
            <p style={{ "clear": "both" }}></p>
            

          </Container>
        </Grid> */}
      </>
    )
};

export default InvoicePreview;
